import React, { useMemo } from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { FeedbacksReportsResponse } from '../../types/reports';
import { format, parseISO } from 'date-fns';

interface NPSModalProps {
  data: FeedbacksReportsResponse;
  comparisonData?: FeedbacksReportsResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

interface NPSInsights {
  npsScore: number;
  promoters: number;
  passives: number;
  detractors: number;
  totalResponses: number;
  averageRating: number;
  promoterPercentage: number;
  passivePercentage: number;
  detractorPercentage: number;
}

const calculateNPS = (feedbackData: FeedbacksReportsResponse): NPSInsights => {
  const friendRatings = feedbackData.feedbacks
    .map(f => f.feedbackData.ratingFriends)
    .filter((rating): rating is number => typeof rating === 'number' && rating >= 1 && rating <= 10);

  if (friendRatings.length === 0) {
    return {
      npsScore: 0,
      promoters: 0,
      passives: 0,
      detractors: 0,
      totalResponses: 0,
      averageRating: 0,
      promoterPercentage: 0,
      passivePercentage: 0,
      detractorPercentage: 0,
    };
  }

  const promoters = friendRatings.filter(r => r >= 9).length;
  const passives = friendRatings.filter(r => r >= 7 && r <= 8).length;
  const detractors = friendRatings.filter(r => r <= 6).length;
  const totalResponses = friendRatings.length;
  const npsScore = ((promoters - detractors) / totalResponses) * 100;
  const averageRating = friendRatings.reduce((sum, rating) => sum + rating, 0) / totalResponses;

  return {
    npsScore,
    promoters,
    passives,
    detractors,
    totalResponses,
    averageRating,
    promoterPercentage: (promoters / totalResponses) * 100,
    passivePercentage: (passives / totalResponses) * 100,
    detractorPercentage: (detractors / totalResponses) * 100,
  };
};

const getNPSCategory = (score: number): { label: string; color: string } => {
  if (score >= 70) return { label: 'Excellent!', color: 'text-emerald-600' };
  if (score >= 30) return { label: 'Good', color: 'text-teal-600' };
  if (score >= 0) return { label: 'Average', color: 'text-yellow-600' };
  return { label: 'Poor', color: 'text-red-600' };
};

// Gauge component for NPS visualization
const NPSGauge: React.FC<{ score: number }> = ({ score }) => {
  // Convert score (-100 to 100) to angle (0 to 180 degrees)
  // Mapping to match the sector distribution
  const scoreToAngle = (value: number) => {
    if (value < 0) {
      // -100 to 0 → 0 to 45 degrees
      return ((value + 100) / 100) * 45;
    } else if (value < 30) {
      // 0 to 30 → 45 to 90 degrees
      return 45 + (value / 30) * 45;
    } else if (value < 70) {
      // 30 to 70 → 90 to 135 degrees
      return 90 + ((value - 30) / 40) * 45;
    } else {
      // 70 to 100 → 135 to 180 degrees
      return 135 + ((value - 70) / 30) * 45;
    }
  };

  const angle = scoreToAngle(score);
  const radius = 90;
  const centerX = 120;
  const centerY = 120;

  // Calculate needle endpoint
  const needleAngle = (180 - angle) * (Math.PI / 180);
  const needleLength = radius - 10;
  const needleX = centerX + needleLength * Math.cos(needleAngle);
  const needleY = centerY - needleLength * Math.sin(needleAngle);

  // Define the colored arcs
  const createArc = (startAngle: number, endAngle: number, isHighlighted: boolean) => {
    const start = startAngle * (Math.PI / 180);
    const end = endAngle * (Math.PI / 180);
    const r = isHighlighted ? radius + 5 : radius;
    
    const x1 = centerX + r * Math.cos(Math.PI - start);
    const y1 = centerY - r * Math.sin(Math.PI - start);
    const x2 = centerX + r * Math.cos(Math.PI - end);
    const y2 = centerY - r * Math.sin(Math.PI - end);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // Determine which zone the score is in
  const isInPoor = score < 0;
  const isInAverage = score >= 0 && score < 30;
  const isInGood = score >= 30 && score < 70;
  const isInExcellent = score >= 70;

  // Calculate label positions outside the arc
  const labelRadius = radius + 28;
  const getLabelPosition = (angleDeg: number) => {
    const angleRad = (180 - angleDeg) * (Math.PI / 180);
    return {
      x: centerX + labelRadius * Math.cos(angleRad),
      y: centerY - labelRadius * Math.sin(angleRad)
    };
  };

  const label_100 = getLabelPosition(0);    // -100 at 0°
  const label0 = getLabelPosition(45);      // 0 at 45°
  const label30 = getLabelPosition(90);     // 30 at 90°
  const label70 = getLabelPosition(135);    // 70 at 135°
  const label100 = getLabelPosition(180);   // 100 at 180°

  return (
    <svg width="280" height="170" viewBox="0 0 280 170" preserveAspectRatio="xMidYMid meet" className="mx-auto">
      <g transform="translate(20, 10)">
        {/* Poor zone (-100 to 0) - Red - 25% (0-45°) */}
        <path
          d={createArc(0, 44, isInPoor)}
          fill="none"
          stroke="#EF4444"
          strokeWidth={isInPoor ? "20" : "16"}
          strokeLinecap="butt"
        />
        
        {/* Average zone (0 to 30) - Yellow - 25% (45-90°) */}
        <path
          d={createArc(46, 89, isInAverage)}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={isInAverage ? "20" : "16"}
          strokeLinecap="butt"
        />
        
        {/* Good zone (30 to 70) - Teal - 25% (90-135°) */}
        <path
          d={createArc(91, 134, isInGood)}
          fill="none"
          stroke="#14B8A6"
          strokeWidth={isInGood ? "20" : "16"}
          strokeLinecap="butt"
        />
        
        {/* Excellent zone (70 to 100) - Green - 25% (135-180°) */}
        <path
          d={createArc(136, 180, isInExcellent)}
          fill="none"
          stroke="#10B981"
          strokeWidth={isInExcellent ? "20" : "16"}
          strokeLinecap="butt"
        />

        {/* Needle */}
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#1F2937"
          strokeWidth="3"
          strokeLinecap="round"
        />
        
        {/* Needle center dot */}
        <circle cx={centerX} cy={centerY} r="5" fill="#1F2937" />

        {/* Score labels positioned outside the arc */}
        <text x={label_100.x +5} y={label_100.y + 5} fontSize="13" fill="#6B7280" fontWeight="600" textAnchor="end">-100</text>
        <text x={label0.x} y={label0.y + 5} fontSize="13" fill="#6B7280" fontWeight="600" textAnchor="middle">0</text>
        <text x={label30.x} y={label30.y +2} fontSize="13" fill="#6B7280" fontWeight="600" textAnchor="middle">30</text>
        <text x={label70.x} y={label70.y + 5} fontSize="13" fill="#6B7280" fontWeight="600" textAnchor="middle">70</text>
        <text x={label100.x } y={label100.y + 5} fontSize="13" fill="#6B7280" fontWeight="600" textAnchor="start">100</text>
      </g>
    </svg>
  );
};

export const NPSModal: React.FC<NPSModalProps> = ({
  data,
  comparisonData,
  isOpen,
  onClose
}) => {
  // Calculate NPS insights - must be before early return
  const npsInsights = useMemo(() => {
    return calculateNPS(data);
  }, [data]);

  const comparisonNpsInsights = useMemo(() => {
    if (!comparisonData) return null;
    return calculateNPS(comparisonData);
  }, [comparisonData]);

  // Calculate trend
  const npsChange = useMemo(() => {
    if (!comparisonNpsInsights) return null;
    const change = npsInsights.npsScore - comparisonNpsInsights.npsScore;
    return {
      value: change,
      trendType: change > 0 ? 'up' as const : change < 0 ? 'down' as const : 'stable' as const
    };
  }, [npsInsights, comparisonNpsInsights]);

  if (!isOpen) return null;

  const npsCategory = getNPSCategory(npsInsights.npsScore);
  const TrendIcon = npsChange?.trendType === 'up' 
    ? TrendingUp 
    : npsChange?.trendType === 'down' 
      ? TrendingDown 
      : Minus;

  const trendColor = npsChange?.trendType === 'up' 
    ? 'text-green-600' 
    : npsChange?.trendType === 'down' 
      ? 'text-red-600' 
      : 'text-rm-neutral-600';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-card shadow-card max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-rm-neutral-600/10">
            <div>
              <h2 className="text-xl font-semibold text-rm-primary-700">
                Net Promoter Score (NPS)
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-rm-bg-50 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors self-start ml-auto"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Card - NPS Gauge */}
              <div className="bg-rm-bg-50 rounded-lg border border-rm-neutral-600/10 p-6">
                <h3 className="text-lg font-semibold text-rm-ink-900 mb-6 text-center">
                  Net Promoter Score
                </h3>
                
                {/* Gauge Chart */}
                <NPSGauge score={npsInsights.npsScore} />
                
                {/* Score Display */}
                <div className="text-center mt-4 mb-6">
                  <div className="text-4xl font-bold text-rm-ink-900">
                    {npsInsights.npsScore.toFixed(0)}
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className={cn('text-sm font-medium', npsCategory.color)}>
                      {npsCategory.label}
                    </span>
                    {npsChange && Math.abs(npsChange.value) >= 0.1 && (
                      <div className={cn('flex items-center gap-1', trendColor)}>
                        <TrendIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {npsChange.value > 0 ? '+' : ''}{npsChange.value.toFixed(1)} pts
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-between items-center text-sm text-base font-medium px-2">
                  <span className="text-red-600">Poor</span>
                  <span className="text-yellow-600">Average</span>
                  <span className="text-teal-600">Good</span>
                  <span className="text-emerald-600">Excellent!</span>
                </div>
              </div>

              {/* Right Card - NPS Breakdown */}
              <div className="bg-rm-bg-50 rounded-lg border border-rm-neutral-600/10 p-6">
                <h3 className="text-lg font-semibold text-rm-ink-900 mb-6">
                  NPS Breakdown
                </h3>

                {/* Formula Row */}
                <div className="flex items-center justify-between mb-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-emerald-600">
                      {npsInsights.promoterPercentage.toFixed(0)}%
                    </div>
                    <div className="text-xs text-rm-neutral-600 mt-1">Promoters</div>
                  </div>
                  
                  <div className="text-2xl font-bold text-rm-neutral-600 px-4">-</div>
                  
                  <div>
                    <div className="text-3xl font-bold text-red-600">
                      {npsInsights.detractorPercentage.toFixed(0)}%
                    </div>
                    <div className="text-xs text-rm-neutral-600 mt-1">Detractors</div>
                  </div>
                  
                  <div className="text-2xl font-bold text-rm-neutral-600 px-4">=</div>
                  
                  <div>
                    <div className="text-3xl font-bold text-rm-primary-700">
                      {npsInsights.npsScore.toFixed(0)}
                    </div>
                    <div className="text-xs text-rm-neutral-600 mt-1">NPS Score</div>
                  </div>
                </div>

                {/* Separator */}
                <div className="border-t border-rm-neutral-600/20 my-6" />

                {/* Category Rows */}
                <div className="space-y-4">
                  {/* Promoters */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-rm-ink-900">
                        Promoters (9/10) <span className="font-bold">{npsInsights.promoters}</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${npsInsights.promoterPercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Passives */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-rm-ink-900">
                        Passives (7/8) <span className="font-bold">{npsInsights.passives}</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-yellow-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${npsInsights.passivePercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Detractors */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-rm-ink-900">
                        Detractors (0-6) <span className="font-bold">{npsInsights.detractors}</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-red-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${npsInsights.detractorPercentage}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Total Responses */}
                <div className="mt-6 pt-4 border-t border-rm-neutral-600/10">
                  <p className="text-sm text-rm-neutral-600">
                    Total responses: {npsInsights.totalResponses} · Period: {format(parseISO(data.summary.dateRange.startDate), 'MMM d')} – {format(parseISO(data.summary.dateRange.endDate), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSModal;
